<template>
  <div id="page">
    <header class="hero">
      <h1 class="title">Efficient Long-Sequence Diffusion Modeling for Symbolic Music Generation</h1>
      <p class="authors">Jinhan Xu<sup>1*</sup>, Xing Tang<sup>1†</sup>, Houpeng Yang<sup>1*</sup>, Haoran Zhang<sup>1</sup>, Shenghua Yuan<sup>1</sup>, Jiatao Chen<sup>1</sup>, Tianming Xie<sup>1</sup>, Jing Wang<sup>4</sup>, Jiaojiao Yu<sup>2,3†</sup>, Guangli Xiang<sup>1</sup></p>
      <p class="affiliations-note">*Co-first authors &nbsp;·&nbsp; †Corresponding authors: <a href="mailto:tangxing@whut.edu.cn">tangxing@whut.edu.cn</a>, <a href="mailto:jojo@hbue.edu.cn">jojo@hbue.edu.cn</a></p>
      <p class="affiliations"><sup>1</sup>School of Computer Science and Artificial Intelligence, Wuhan University of Technology, Wuhan, China &nbsp;·&nbsp; <sup>2</sup>School of Information Engineering, Hubei University of Economics, Wuhan, China &nbsp;·&nbsp; <sup>3</sup>Hubei Key Laboratory of Digital Finance Innovation, Wuhan, China &nbsp;·&nbsp; <sup>4</sup>School of Computer Science, Hubei University of Technology, Wuhan, China</p>
      <div class="links">
        <a href="https://github.com/3328702107/Supplementary_Material" target="_blank" rel="noopener" class="link">Code Repo</a>
      </div>
      <nav class="topnav">
        <a href="#demos">Demos</a>
        <a href="#diy">DIY</a>
      </nav>
    </header>

    <main class="content">
      <section id="intro" class="section">
        <p>
          This page presents three non-conditional demo audios generated by our method, each produced on a different dataset. 
        </p>
      </section>
      

      

      <section id="demos" class="section">
        <h2>Demo Audios</h2>
        <p>Three demo audios generated on different datasets to illustrate quality and style.</p>

        <div class="demo-list">
          <div class="demo-item example-card warm">
            <h3>Demo 1 </h3>
              <div class="player">
              <button class="btn" @click="playMidi(1)" :disabled="isLoading && playingIndex===1">▶ Play</button>
              <button class="btn outline" @click="pauseMidi" :disabled="playingIndex!==1 || isPaused">⏸ Pause</button>
              <button class="btn outline" @click="playMidi(1)" :disabled="playingIndex!==1 || !isPaused">▶ Resume</button>
              <button class="btn outline" @click="stopMidi" :disabled="playingIndex!==1">■ Stop</button>
                <a class="btn link" href="audio/demo1.midi" download>Download</a>
              <input class="slider" type="range" min="0" max="100" :value="Math.round(progressList[0]*100)"
                @input="onSliderInput($event, 1)" @change="onSliderChange($event, 1)" />
              <div class="time">{{ formatTime(progressList[0]*durationList[0]) }} / {{ formatTime(durationList[0]) }}</div>
              </div>
          </div>
          <div class="demo-item example-card warm">
            <h3>Demo 2 </h3>
              <div class="player">
              <button class="btn" @click="playMidi(2)" :disabled="isLoading && playingIndex===2">▶ Play</button>
              <button class="btn outline" @click="pauseMidi" :disabled="playingIndex!==2 || isPaused">⏸ Pause</button>
              <button class="btn outline" @click="playMidi(2)" :disabled="playingIndex!==2 || !isPaused">▶ Resume</button>
              <button class="btn outline" @click="stopMidi" :disabled="playingIndex!==2">■ Stop</button>
                <a class="btn link" href="audio/demo2.midi" download>Download</a>
              <input class="slider" type="range" min="0" max="100" :value="Math.round(progressList[1]*100)"
                @input="onSliderInput($event, 2)" @change="onSliderChange($event, 2)" />
              <div class="time">{{ formatTime(progressList[1]*durationList[1]) }} / {{ formatTime(durationList[1]) }}</div>
              </div>
          </div>
          <div class="demo-item example-card warm">
            <h3>Demo 3 </h3>
              <div class="player">
              <button class="btn" @click="playMidi(3)" :disabled="isLoading && playingIndex===3">▶ Play</button>
              <button class="btn outline" @click="pauseMidi" :disabled="playingIndex!==3 || isPaused">⏸ Pause</button>
              <button class="btn outline" @click="playMidi(3)" :disabled="playingIndex!==3 || !isPaused">▶ Resume</button>
              <button class="btn outline" @click="stopMidi" :disabled="playingIndex!==3">■ Stop</button>
                <a class="btn link" href="audio/demo3.midi" download>Download</a>
              <input class="slider" type="range" min="0" max="100" :value="Math.round(progressList[2]*100)"
                @input="onSliderInput($event, 3)" @change="onSliderChange($event, 3)" />
              <div class="time">{{ formatTime(progressList[2]*durationList[2]) }} / {{ formatTime(durationList[2]) }}</div>
              </div>
          </div>
        </div>
      </section>

      <section id="diy" class="section">
        <h2>DIY in Real Time!</h2>
        <p>Try our interactive tool to generate accompaniments from your own melody and chords.</p>
        <p><a class="cta" href="https://github.com/3328702107/smdim-music" target="_blank" rel="noopener">Try the Interactive Demo →</a></p>
      </section>
    </main>
    <button
      v-show="showTop"
      class="back-to-top"
      @click="scrollToTop"
      aria-label="Back to top"
      title="Back to top"
    >↑ Top</button>
  </div>
</template>

<script>
export default {
  name: 'App',
  components: {},
  data() {
    return {
      showTop: false,
      playingIndex: null,
      isLoading: false,
      // Per-demo progress and duration
      progressList: [0, 0, 0],
      durationList: [0, 0, 0],
      // Playback state
      isPaused: false,
      audioCtx: null,
      player: null,
      events: [],
      startTime: 0,
      pausedAt: 0,
      rafId: null,
      isDragging: false,
      lastSeekAt: 0,
    }
  },
  mounted() {
    this.onScroll()
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  unmounted() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() {
      const y = window.scrollY || document.documentElement.scrollTop
      this.showTop = y > 400
    },
    formatTime(seconds) {
      const s = Math.max(0, Math.floor(seconds || 0))
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${m}:${r.toString().padStart(2, '0')}`
    },
    onSliderInput(e, idx) {
      // Visual preview while dragging
      if (this.playingIndex !== idx) return
      const val = Number(e.target.value || 0)
      const percent = Math.min(1, Math.max(0, val / 100))
      const idx0 = idx - 1
      this.isDragging = true
      // Update progress visually and time text
      this.progressList[idx0] = percent
      // Realtime seek while playing: throttle to avoid excessive re-scheduling
      const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
      if (!this.isPaused && (now - this.lastSeekAt > 120)) {
        this.lastSeekAt = now
        this.seekTo(idx, percent)
      }
    },
    onSliderChange(e, idx) {
      if (this.playingIndex !== idx) return
      const val = Number(e.target.value || 0)
      const percent = Math.min(1, Math.max(0, val / 100))
      this.isDragging = false
      this.seekTo(idx, percent)
    },
    async seekTo(idx, percent) {
      try {
        const idx0 = idx - 1
        const dur = this.durationList[idx0] || 0
        const targetOffset = dur * percent
        // If paused, just update pausedAt and progress, allow resume later
        if (this.isPaused) {
          this.pausedAt = targetOffset
          this.progressList[idx0] = Math.min(1, percent)
          return
        }
        // If playing, restart from target offset
        if (this.playingIndex === idx) {
          // Stop current audio without clearing playingIndex
          if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null }
          if (this.player && typeof this.player.stop === 'function') {
            try { this.player.stop() } catch (x) { console.debug('player.stop failed during seek', x) }
          }
          if (this.audioCtx && typeof this.audioCtx.close === 'function') {
            try { await this.audioCtx.close() } catch (x) { console.debug('audioCtx.close failed during seek', x) }
          }
          this.player = null
          this.audioCtx = null
          // set pausedAt to targetOffset then resume
          this.pausedAt = targetOffset
          this.isPaused = true
          await this.resumeMidi()
        }
      } catch (err) {
        console.warn('seek error', err)
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async playMidi(idx) {
      try {
        // Resume if paused on the same track
        if (this.playingIndex === idx && this.isPaused) {
          await this.resumeMidi()
          return
        }

        // Stop any current playback and start fresh
        if (this.playingIndex !== null) await this.stopMidi()
        this.isLoading = true
        this.playingIndex = idx

        const base = process.env.BASE_URL || '/'
        const fileMap = {
          1: base + 'audio/demo1.midi',
          2: base + 'audio/demo2.midi',
          3: base + 'audio/demo3.midi'
        }
        const url = fileMap[idx]
        if (!url) return

        const { Midi } = await import('@tonejs/midi')
        const Soundfont = (await import('soundfont-player')).default

        // Prepare WebAudio context
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        this.audioCtx = new AudioCtx()

        const resp = await fetch(url)
        if (!resp.ok) {
          throw new Error('Failed to fetch MIDI: ' + resp.status)
        }
        const arrayBuffer = await resp.arrayBuffer()
        const midi = new Midi(arrayBuffer)

        // Load instrument (acoustic grand piano)
        this.player = await Soundfont.instrument(this.audioCtx, 'acoustic_grand_piano')

        // Collect events
        const events = []
        midi.tracks.forEach(track => {
          track.notes.forEach(note => {
            events.push({
              time: note.time,
              name: note.name,
              duration: note.duration,
              velocity: note.velocity ?? 0.8
            })
          })
        })
        this.events = events
        // Schedule notes relative to context time
        this.startTime = this.audioCtx.currentTime + 0.05
        this.events.forEach(ev => {
          this.player.play(ev.name, this.startTime + (ev.time || 0), {
            gain: ev.velocity ?? 0.8,
            duration: ev.duration || 0.5
          })
        })

        // Compute duration from MIDI or events
        const lastEnd = events.reduce((m, e) => Math.max(m, (e.time || 0) + (e.duration || 0)), 0)
        const idx0 = idx - 1
        this.durationList[idx0] = midi.duration || lastEnd || 10
        this.progressList[idx0] = 0
        this.isPaused = false
        console.log('[midi] start playback, duration:', this.durationList[idx0])

        // Progress via requestAnimationFrame
        const tick = () => {
          const t = Math.max(0, (this.audioCtx.currentTime - this.startTime))
          this.progressList[idx0] = Math.min(1, t / this.durationList[idx0])
          if (t >= this.durationList[idx0]) {
            this.progressList[idx0] = 1
            this.stopMidi()
            return
          }
          this.rafId = requestAnimationFrame(tick)
        }
        this.rafId = requestAnimationFrame(tick)

      } catch (e) {
        console.error('MIDI play error:', e)
      } finally {
        this.isLoading = false
      }
    },
    async pauseMidi() {
      try {
        if (this.playingIndex !== null && !this.isPaused) {
          this.isPaused = true
          // Record paused position
          if (this.audioCtx) {
            this.pausedAt = Math.max(0, this.audioCtx.currentTime - this.startTime)
          }
          // Stop progress loop
          if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null }
          // Cancel current audio (soundfont-player has no pause; stop by closing context)
          if (this.player && typeof this.player.stop === 'function') {
            try { this.player.stop() } catch (x) { console.debug('player.stop failed during pause', x) }
          }
          if (this.audioCtx && typeof this.audioCtx.close === 'function') {
            try { await this.audioCtx.close() } catch (x) { console.debug('audioCtx.close failed during pause', x) }
          }
          this.player = null
          this.audioCtx = null
        }
      } catch (e) {
        console.warn('pause error', e)
      }
    },
    async resumeMidi() {
      try {
        if (this.playingIndex === null || !this.isPaused) return
        const idx = this.playingIndex
        const { Midi } = await import('@tonejs/midi')
        const Soundfont = (await import('soundfont-player')).default
        const base = process.env.BASE_URL || '/'
        const fileMap = { 1: base + 'audio/demo1.midi', 2: base + 'audio/demo2.midi', 3: base + 'audio/demo3.midi' }
        const url = fileMap[idx]
        const resp = await fetch(url)
        if (!resp.ok) throw new Error('Failed to fetch MIDI: ' + resp.status)
        const arrayBuffer = await resp.arrayBuffer()
        const midi = new Midi(arrayBuffer)

        // Recreate context and player
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        this.audioCtx = new AudioCtx()
        this.player = await Soundfont.instrument(this.audioCtx, 'acoustic_grand_piano')

        // Schedule remaining notes starting at pausedAt
        const offset = this.pausedAt || 0
        this.startTime = this.audioCtx.currentTime - offset
        const idx0 = idx - 1
        this.progressList[idx0] = Math.min(1, offset / (this.durationList[idx0] || 1))

        const events = []
        midi.tracks.forEach(track => {
          track.notes.forEach(note => {
            events.push({
              time: note.time,
              name: note.name,
              duration: note.duration,
              velocity: note.velocity ?? 0.8
            })
          })
        })
        // Schedule only events with time >= offset
        events.filter(ev => (ev.time || 0) >= offset - 1e-3).forEach(ev => {
          this.player.play(ev.name, this.audioCtx.currentTime + ((ev.time || 0) - offset), {
            gain: ev.velocity ?? 0.8,
            duration: ev.duration || 0.5
          })
        })

        this.isPaused = false
        const tick = () => {
          const t = Math.max(0, (this.audioCtx.currentTime - (this.startTime)))
          this.progressList[idx0] = Math.min(1, t / this.durationList[idx0])
          if (t >= this.durationList[idx0]) { this.stopMidi(); return }
          this.rafId = requestAnimationFrame(tick)
        }
        this.rafId = requestAnimationFrame(tick)
      } catch (e) {
        console.warn('resume error', e)
      }
    },
    async stopMidi() {
      try {
        if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null }
        this.isPaused = false
        if (this.player && typeof this.player.stop === 'function') {
          try { this.player.stop() } catch (x) { console.debug('player.stop failed during stop', x) }
        }
        this.player = null
        if (this.audioCtx && typeof this.audioCtx.close === 'function') {
          try { await this.audioCtx.close() } catch (x) { console.debug('audioCtx.close failed during stop', x) }
        }
        this.audioCtx = null
        this.events = []
        this.startTime = 0
        this.pausedAt = 0
        this.playingIndex = null
      } catch (e) {
        console.warn('MIDI stop warning:', e)
        this.playingIndex = null
      }
    }
  }
}
</script>

<style>
:root {
  --text: #1a1a1a;
  --muted: #5b5b5b;
  --maxw: 960px;
  --warm-bg: #fde8c9;
  --cool-bg: #d9f5f0;
  --card-radius: 16px;
}

#page {
  color: var(--text);
}

.hero {
  max-width: var(--maxw);
  margin: 48px auto 24px;
  text-align: center;
}

.title {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 12px;
}

.topnav {
  position: sticky;
  top: 0;
  display: inline-flex;
  gap: 12px;
  background: #fff;
  padding: 8px 12px;
  border-radius: 999px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
}
.topnav a { color: #111; text-decoration: none; padding: 4px 10px; border-radius: 999px; }
.topnav a:hover { background: #f3f4f6; }

.authors {
  color: var(--muted);
  margin: 6px 0 10px;
}
.affiliations { color: var(--muted); font-size: 13px; margin-top: 2px; }
.affiliations-note { color: var(--muted); font-size: 13px; margin: 4px 0 2px; }

.links { margin-top: 6px; }
.link { color: #3b82f6; text-decoration: none; margin: 0 8px; }
.link:hover { text-decoration: underline; }

.content { max-width: var(--maxw); margin: 0 auto 80px; }
.section { margin: 28px 0; }
.section h2 { font-size: 28px; margin: 18px 0; }
.section p { color: var(--text); line-height: 1.7; }
/* Limit paragraph line length for readability */
.section p, .section ul { max-width: 760px; }
/* Accessible visually-hidden utility */
.visually-hidden { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0; }
.section ul { margin: 8px 0 16px 18px; }

.example-card {
  border-radius: var(--card-radius);
  padding: 18px;
  margin: 18px 0;
}
.example-card.warm { background: var(--warm-bg); }
.example-card.cool { background: var(--cool-bg); }

.example-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.col h3 { margin: 0 0 6px; }
.sub { color: var(--muted); margin: 8px 0; }
.audio { width: 100%; }
.player { display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
.btn { padding:8px 12px; border-radius:8px; border:1px solid #e5e7eb; background:#111827; color:#fff; cursor:pointer; }
.btn.outline { background:#fff; color:#111827; }
.btn.link { background:#fff; color:#2563eb; border-color:#bfdbfe; text-decoration:none; }
.btn:disabled { opacity:.6; cursor:not-allowed; }
.progress { position: relative; flex: 1 1 160px; height: 8px; background: #e5e7eb; border-radius: 999px; overflow: hidden; }
.progress .bar { position:absolute; left:0; top:0; bottom:0; width:0%; background:#3b82f6; }
.time { font-size: 12px; color: var(--muted); }
/* Slider style for seek */
.slider { appearance: none; -webkit-appearance: none; width: 160px; height: 6px; border-radius: 999px; background: #e5e7eb; outline: none; }
.slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 14px; height: 14px; border-radius: 50%; background: #3b82f6; cursor: pointer; }
.slider::-moz-range-thumb { width: 14px; height: 14px; border-radius: 50%; background: #3b82f6; cursor: pointer; }

.abl-grid { display: grid; grid-template-columns: 320px 1fr; gap: 18px; align-items: start; }
.img-placeholder { background: #fff7ed; border: 1px dashed #f59e0b; color:#a16207; padding: 24px; border-radius: 12px; text-align:center; }
.audio-row { display: grid; grid-template-columns: 140px 1fr; gap: 10px; align-items: center; margin: 8px 0; }
.tag { display:inline-block; padding: 4px 8px; background:#e5e7eb; border-radius:999px; font-size:12px; color:#374151; }
.tag.primary { background:#dbeafe; color:#1e40af; }
.cta { display:inline-block; background:#111827; color:#fff; text-decoration:none; padding:10px 14px; border-radius:10px; }
.cta:hover { opacity:.9; }

.back-to-top {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 1000;
  padding: 10px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #111827;
  color: #fff;
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  cursor: pointer;
}
.back-to-top:hover { opacity: .92; }
.back-to-top:focus { outline: 2px solid #93c5fd; }

/* Responsive breakpoints */
@media (max-width: 900px) {
  .title { font-size: 32px; }
  .hero { margin: 32px auto 16px; }
  .content { margin-bottom: 64px; padding: 0 12px; }
  .example-grid { grid-template-columns: 1fr; }
  .abl-grid { grid-template-columns: 1fr; }
  .audio-row { grid-template-columns: 1fr; }
  .topnav { display:flex; flex-wrap: wrap; gap: 8px; padding: 6px 10px; }
}

@media (max-width: 640px) {
  .title { font-size: 26px; }
  .example-card { padding: 14px; border-radius: 12px; }
  .img-placeholder { padding: 16px; }
}

html, body, #app { height: 100%; }
body { font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif; margin: 0; }
.title { letter-spacing: .5px; font-weight:600; }
h2 { letter-spacing:.3px; font-weight:600; }
/* Tag focus & link focus styles */
.link:focus, .topnav a:focus, .cta:focus, .tag:focus { outline:2px solid #2563eb; outline-offset:2px; }
</style>
